/**
 * upload/useUploadOverlay.ts — the upload journey's logic, extracted so two skins can share it.
 *
 * WHY THIS EXISTS. `UploadOverlay.tsx` used to be state + handlers + JSX in one file. We now ship the
 * same journey behind a per-browser toggle (classic vs a reskinned Figma look — see skinStore.ts), and
 * the ONE thing that must not fork is the behaviour: the phase machine, the draft autosave, the gates,
 * the error paths. So all of that lives here, verbatim, and each skin is JUST the JSX that reads what
 * this hook returns. A diff of the classic JSX against the old `return` shows only `vm.` prefixes.
 *
 * Everything below is a straight move from the old component body — no logic changed. The three
 * callbacks that used to be written inline in the JSX (`onToggleCollapse`, and the two `registerField`s)
 * are pulled up here too, so both skins wire them identically.
 *
 * See UploadOverlay.tsx's original header for the flow contract: NO useEffect, autosave inside the
 * updater, a ref-guarded one-shot open sequence, and a draft that is only ever cleared on a successful
 * upload or an explicit discard.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  allExplained,
  explainedCount,
  missingExplanations,
  reconcileExplanations,
  toComponentDocs,
  type ExplanationMap,
  type FlowComponent,
} from '@trailblazer/shared';
import {
  apiPost,
  checkDuplicates,
  inspectComponents,
  type CloudFlowSummary,
  type ComponentDocsIncomplete,
  type DuplicateCandidate,
  type ToolRefData,
  type UploadRequest,
  type UploadResult,
} from '../api';
import {
  clearDraft,
  clearOtherDrafts,
  emptyDetails,
  loadDraft,
  newDraft,
  saveDraft,
  type DraftDetails,
  type UploadDraft,
  type UploadStage,
} from './draftStore';
import { detailsValid, firstInvalidField, validateDetails, type DetailsField } from './validation';

/** The overlay's phase. Everything the builder can be looking at is one of these. */
export type Phase =
  | { kind: 'loading' }
  /** The automation's definition could not be turned into components — a dead end WITH a way back. */
  | { kind: 'unavailable'; message: string }
  /** A draft exists for this automation: Resume or Start new (Will's "Resume or Start New Prompt"). */
  | { kind: 'resume'; draft: UploadDraft }
  /** Possible duplicates found (UP-F27/F28). Soft: the builder chooses whether to carry on. */
  | { kind: 'duplicates'; candidates: DuplicateCandidate[]; sameFlow: boolean }
  | { kind: 'wizard' }
  | { kind: 'done'; result: UploadResult };

export const STAGE_ORDER: UploadStage[] = ['details', 'instructions', 'review'];
export const STAGE_LABELS: Record<UploadStage, string> = {
  details: 'Details',
  instructions: 'Instructions',
  review: 'Review',
};

export interface UploadOverlayProps {
  flow: CloudFlowSummary;
  environmentId: string;
  environmentName: string;
  refData: ToolRefData;
  /** For the per-user draft key, and to show the builder their own identity on Details. */
  userId: string;
  builderName: string;
  builderDepartment: string | null;
  /** Third row of the design's identity card, whose fixed 161px height assumes three pairs. */
  builderJobTitle: string | null;
  /**
   * Contact address shown on the identity card (UP-F02: name, contact address, department are
   * pre-filled from the user record and read-only). From the signed-in user's `me.email`.
   */
  builderEmail: string | null;
  /** Whether the selected flow's blueprint has connectors — makes Requirements mandatory. */
  connectorState: 'unknown' | 'checking' | 'has' | 'none' | 'error';
  onClose: () => void;
  /** Called after a successful upload so the picker can refresh its draft markers. */
  onUploaded: (result: UploadResult) => void;
  /**
   * Defer the `onUploaded` call to a completion screen instead of firing it the
   * instant the upload succeeds.
   *
   * WHY. `onUploaded` (Share.tsx) both shows the "shared as a Draft" banner AND
   * closes the overlay. V1/V2 have no dedicated success PAGE — their `done` view is
   * a small card dismissed via `onClose` — so firing `onUploaded` on success is
   * correct there. V3 DOES have a success page (P5 "Publish your tool"), reached by
   * `phase.kind === 'done'`; if `onUploaded` fires on success it closes the overlay
   * in the same tick, tearing P5 down before it ever paints (the bug: upload works,
   * but the confirmation page is skipped and the list banner shows instead). So V3
   * passes `deferCompletion: true` and lets P5's own "Finish" button call
   * `props.onUploaded` — the placement the Publish page was always written for
   * (see UploadPublishPage.tsx). Defaults false so V1/V2 are untouched.
   */
  deferCompletion?: boolean;
  /**
   * Skip the "Resume or Start new" prompt and restore the existing draft straight away.
   *
   * WHY. Under V3 the "continue or start new?" decision is now made ONCE, up front, on the pre-picker
   * gate (see UploadOverlayPages). When the builder chose "continue" there, the flow is opened purely
   * to resume it — so re-asking the same question the instant the wizard mounts is a duplicate prompt.
   * With this set, the opening sequence runs the SAME restore path `onResume` would (the explanation
   * reconcile against the flow-as-it-is-now is preserved), it just does not stop to ask first.
   *
   * Only the gate's "Continue draft" sets it. A plain row pick / "Start new" leaves it false, so the
   * per-flow resume prompt still appears for any draft reached WITHOUT going through the gate (there is
   * none in the single-draft model today, but the hook stays correct if one ever is). Defaults false so
   * V1/V2 and the picker path are untouched.
   */
  autoResumeDraft?: boolean;
}

/**
 * The whole upload journey as a hook. Returns every value and handler the JSX reads; the two skins
 * (classic and reskin) each render their own markup off this single source of behaviour.
 */
export function useUploadOverlay(props: UploadOverlayProps) {
  const defaults = useMemo(
    () => ({
      categoryId:
        props.refData.categories.find((o) => o.label === 'Automations')?.id ??
        props.refData.categories[0]?.id ??
        0,
      accessId:
        props.refData.accessRequirements.find((o) => o.label === 'All employees')?.id ??
        props.refData.accessRequirements[0]?.id ??
        0,
      departmentId:
        props.refData.departments.find((o) => o.label === 'Digital & IT')?.id ??
        props.refData.departments[0]?.id ??
        0,
    }),
    [props.refData],
  );

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [components, setComponents] = useState<FlowComponent[]>([]);
  const [fingerprint, setFingerprint] = useState('');
  const [draft, setDraft] = useState<UploadDraft | null>(null);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  /** Per-stage "the builder has tried to continue", so errors appear on demand rather than up front. */
  const [triedStage, setTriedStage] = useState<Record<UploadStage, boolean>>({
    details: false,
    instructions: false,
    review: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [droppedCount, setDroppedCount] = useState(0);
  /** Duplicates found at open, kept so "Details" can re-show them if the builder changes the name. */
  const [pendingDuplicates, setPendingDuplicates] = useState<{
    candidates: DuplicateCandidate[];
    sameFlow: boolean;
  } | null>(null);

  const dialogRef = useRef<HTMLDivElement | null>(null);
  const detailFieldRefs = useRef<Partial<Record<DetailsField, HTMLElement | null>>>({});
  const componentFieldRefs = useRef<Map<string, HTMLTextAreaElement | null>>(new Map());
  const openedRef = useRef(false);

  const stage: UploadStage = draft?.stage ?? 'details';
  const details: DraftDetails = draft?.details ?? emptyDetails(defaults);
  const explanations: ExplanationMap = draft?.explanations ?? {};
  const requirementsRequired = props.connectorState === 'has';

  const detailErrors = useMemo(
    () => validateDetails(details, { requirementsRequired }),
    [details, requirementsRequired],
  );
  const missingIds = useMemo(
    () => missingExplanations(components, explanations),
    [components, explanations],
  );
  const explained = explainedCount(components, explanations);
  const instructionsComplete = allExplained(components, explanations);

  // UP-F45. The platform's moderation posture, threaded from the loaded refData, drives the submit
  // button's LABEL only ('Publish' when self_moderated, 'Submit for review' when manual_review). It is
  // never a gate — the same read the server used to route the submission, so the label cannot disagree
  // with where the tool actually lands. `refData.moderationMode` is always present (server default is
  // manual_review), so no runtime fallback is needed here.
  const moderationMode = props.refData.moderationMode;

  // UP-F45. The still-incomplete required items, in the SAME order onUpload re-checks its gates
  // (details, then instructions, then the attestation tick). Rendered as a pre-press hint beside the
  // submit button; it does NOT disable the button — clicking with items outstanding focuses the first
  // offender via onUpload's existing focus-the-problem paths. Empty when everything is satisfied.
  const outstandingItems = useMemo(() => {
    const items: string[] = [];
    if (!detailsValid(detailErrors)) items.push('Complete the tool details');
    if (!instructionsComplete) items.push('Explain every step');
    if (!(draft?.attested ?? false)) items.push('Confirm the data-safety attestation');
    return items;
  }, [detailErrors, instructionsComplete, draft?.attested]);

  /**
   * Update the draft AND persist it in one step. Autosave is part of every mutation rather than a
   * reaction to it, so there is no window in which state has moved on but storage has not.
   */
  const updateDraft = useCallback(
    (patch: (current: UploadDraft) => UploadDraft) => {
      setDraft((current) => {
        if (!current) return current;
        const next = { ...patch(current), updatedAt: new Date().toISOString() };
        saveDraft(props.userId, next);
        return next;
      });
    },
    [props.userId],
  );

  // ── Opening sequence ───────────────────────────────────────────────────────────────────────────
  //
  // Runs exactly once. Inspects the automation and checks for duplicates in parallel (neither depends
  // on the other), then decides which prompt the builder sees first, per Will's flow: draft check
  // BEFORE duplicate handling.
  if (!openedRef.current) {
    openedRef.current = true;
    void Promise.resolve().then(async () => {
      const [inspected, duplicates] = await Promise.all([
        inspectComponents(props.environmentId, props.flow.workflowId),
        checkDuplicates({ workflowId: props.flow.workflowId }),
      ]);

      if (inspected.kind === 'unauthorized') {
        setPhase({
          kind: 'unavailable',
          message: 'Your session has expired. Sign in again and retry.',
        });
        return;
      }
      if (inspected.kind === 'error') {
        setPhase({
          kind: 'unavailable',
          message: describeInspectFailure(inspected.status, inspected.code, inspected.detail),
        });
        return;
      }

      const manifest = inspected.body;
      setComponents(manifest.components);
      setFingerprint(manifest.definition_fingerprint);

      const dupes =
        duplicates.kind === 'ok' && duplicates.body.candidates.length > 0
          ? { candidates: duplicates.body.candidates, sameFlow: duplicates.body.same_flow }
          : null;
      setPendingDuplicates(dupes);

      // Will's flow: check for an existing draft first.
      const existing = loadDraft(props.userId, props.flow.workflowId);
      if (existing) {
        // The gate already asked "continue or start new?" — if the builder chose continue, restore
        // straight away rather than re-asking with a second, identical prompt (see autoResumeDraft).
        // The restore still reconciles explanations against the flow's CURRENT components, exactly as
        // the manual Resume would; we pass the fresh manifest values because the state setters above
        // have not flushed yet inside this closure.
        if (props.autoResumeDraft) {
          restoreDraft(existing, manifest.components, manifest.definition_fingerprint, dupes);
        } else {
          setPhase({ kind: 'resume', draft: existing });
        }
        return;
      }

      const fresh = newDraft({
        workflowId: props.flow.workflowId,
        sourceName: manifest.source_name || props.flow.name,
        definitionFingerprint: manifest.definition_fingerprint,
        details: emptyDetails(defaults),
        nowIso: new Date().toISOString(),
      });
      setDraft(fresh);
      // Single-draft model: beginning a new upload replaces any earlier unfinished one, so the
      // surface only ever offers to continue the latest. (The fresh draft is saved by the first
      // autosave; evict siblings now so a stale one cannot outlive this one.)
      clearOtherDrafts(props.userId, props.flow.workflowId);
      setPhase(dupes ? { kind: 'duplicates', ...dupes } : { kind: 'wizard' });
    });
  }

  /**
   * Restore a saved draft into the wizard, reconciling its explanations against the flow's components
   * as they are NOW. Shared by the manual Resume button (`onResume`) and the gate's auto-resume path
   * in the opening sequence — so both restore identically, and the reconcile is never skipped.
   *
   * `comps`/`fp`/`dupes` are passed in rather than read from state because the opening-sequence caller
   * runs inside an async closure BEFORE the corresponding setState calls have flushed; `onResume` runs
   * later and hands in the current state values. Same logic either way.
   */
  function restoreDraft(
    saved: UploadDraft,
    comps: FlowComponent[],
    fp: string,
    dupes: { candidates: DuplicateCandidate[]; sameFlow: boolean } | null,
  ) {
    // The author may have edited their flow since. Keep what still matches, drop what does not, and
    // say so — silently re-attaching an explanation to a different step would be worse than losing it.
    const { kept, droppedIds } = reconcileExplanations(comps, saved.explanations);
    setDroppedCount(droppedIds.length);
    const restored: UploadDraft = {
      ...saved,
      definitionFingerprint: fp,
      explanations: kept,
      // If the flow changed, land them on Instructions — that is where the gap is.
      stage: droppedIds.length > 0 ? 'instructions' : saved.stage,
    };
    setDraft(restored);
    saveDraft(props.userId, restored);
    // Duplicates were already acknowledged in the earlier session; do not interrogate them again.
    setPhase(
      !saved.duplicateAcknowledged && dupes ? { kind: 'duplicates', ...dupes } : { kind: 'wizard' },
    );
  }

  /** Resume: restore the draft, reconciling explanations against the flow as it is NOW. */
  function onResume() {
    if (phase.kind !== 'resume') return;
    restoreDraft(phase.draft, components, fingerprint, pendingDuplicates);
  }

  /** Start new: discard the existing draft and begin again from scratch. */
  function onStartNew() {
    clearDraft(props.userId, props.flow.workflowId);
    setDroppedCount(0);
    const fresh = newDraft({
      workflowId: props.flow.workflowId,
      sourceName: props.flow.name,
      definitionFingerprint: fingerprint,
      details: emptyDetails(defaults),
      nowIso: new Date().toISOString(),
    });
    setDraft(fresh);
    saveDraft(props.userId, fresh);
    // Single-draft model: this fresh start is now the only draft — evict any other flow's leftover.
    clearOtherDrafts(props.userId, props.flow.workflowId);
    setPhase(pendingDuplicates ? { kind: 'duplicates', ...pendingDuplicates } : { kind: 'wizard' });
  }

  /** "Mine is different" — carry on, and remember so the prompt is not repeated. */
  function onDuplicatesContinue() {
    updateDraft((d) => ({ ...d, duplicateAcknowledged: true }));
    setPhase({ kind: 'wizard' });
  }

  /** "It does the same thing" — do not upload another copy. Discard the draft and leave. */
  function onDuplicatesAbandon() {
    clearDraft(props.userId, props.flow.workflowId);
    props.onClose();
  }

  function goToStage(next: UploadStage) {
    updateDraft((d) => ({ ...d, stage: next }));
    // Return focus to the top of the dialog so a screen reader announces the new stage.
    window.requestAnimationFrame(() => dialogRef.current?.focus());
  }

  /** Focus and scroll to one Details field. */
  function focusDetailField(field: DetailsField) {
    const el = detailFieldRefs.current[field];
    el?.scrollIntoView({ block: 'center', behavior: 'auto' });
    el?.focus();
  }

  /**
   * Focus and scroll to one component's explanation, expanding it first if collapsed.
   *
   * The retry is not defensive padding: this is called both after expanding a collapsed section AND
   * after switching stage from Review, so the target may be hidden or not yet mounted when the first
   * frame runs. A hidden textarea silently refuses focus, which would leave the builder staring at an
   * unchanged screen after pressing Next — the exact failure the "take me to the problem" behaviour
   * exists to prevent. So we re-check for a few frames and give up quietly rather than guess.
   */
  function focusComponent(componentId: string) {
    setCollapsedIds((prev) => {
      if (!prev.has(componentId)) return prev;
      const next = new Set(prev);
      next.delete(componentId);
      return next;
    });

    let attempts = 0;
    const tryFocus = () => {
      const el = componentFieldRefs.current.get(componentId);
      // offsetParent is null while the section is still hidden — not yet focusable.
      if (el && el.offsetParent !== null) {
        el.scrollIntoView({ block: 'center', behavior: 'auto' });
        el.focus();
        return;
      }
      if (attempts < 5) {
        attempts += 1;
        window.requestAnimationFrame(tryFocus);
      }
    };
    window.requestAnimationFrame(tryFocus);
  }

  /**
   * Details → Instructions. Blocked while anything required is missing: we stay on Details, reveal the
   * messages, and move focus to the first incomplete field. The Next button is never merely disabled
   * with no explanation.
   */
  function onNextFromDetails() {
    setTriedStage((t) => ({ ...t, details: true }));
    if (!detailsValid(detailErrors)) {
      const first = firstInvalidField(detailErrors);
      if (first) focusDetailField(first);
      return;
    }
    goToStage('instructions');
  }

  /**
   * Instructions → Review. THE STRICT GATE: every component of this automation must carry an adequate
   * explanation. Validation runs over the whole component list, never only what is expanded.
   */
  function onNextFromInstructions() {
    setTriedStage((t) => ({ ...t, instructions: true }));
    if (!instructionsComplete) {
      const firstMissing = missingIds[0];
      if (firstMissing) focusComponent(firstMissing);
      return;
    }
    goToStage('review');
  }

  /** Upload. Re-checks both gates, because a builder can reach Review and then edit backwards. */
  async function onUpload() {
    if (!draft) return;
    setTriedStage((t) => ({ ...t, review: true }));
    setErrorMsg(null);

    if (!detailsValid(detailErrors)) {
      setErrorMsg('Some details are incomplete. Open Details to fix them.');
      goToStage('details');
      return;
    }
    if (!instructionsComplete) {
      setErrorMsg('Every step needs an explanation before you can share this.');
      goToStage('instructions');
      return;
    }
    if (!draft.attested) {
      // Stay on Review: the message is beside the tick, which is where the builder is looking.
      return;
    }

    setSubmitting(true);
    const body: UploadRequest = {
      targetEnvironmentId: props.environmentId,
      workflowId: props.flow.workflowId,
      name: details.name.trim(),
      description: details.description.trim(),
      problem_solved: details.problemSolved.trim(),
      primary_category_id: details.categoryId,
      access_requirement_id: details.accessId,
      department_id: details.departmentId,
      dependencies: details.dependencies.trim(),
      estimated_time_saved: Number(details.estimatedTimeSaved),
      // Gated above (attested) and routing-controlling (takesResponsibility). The backend re-checks
      // the attestation and routes the tool to Draft (vouched) or Pending (AI SWAT team) on this flag.
      attested: draft.attested,
      takes_responsibility: draft.takesResponsibility,
      // The backend renders setup_instructions from these, so no separate textarea is sent.
      component_docs: toComponentDocs(components, explanations),
      ...(details.requirements.trim()
        ? { connector_setup_notes: details.requirements.trim() }
        : {}),
      ...(details.tags.length > 0 ? { tags: details.tags } : {}),
    };

    const res = await apiPost<UploadResult>('/api/tools', body);
    setSubmitting(false);

    if (res.kind === 'ok') {
      // The one place besides an explicit discard where a draft is cleared.
      clearDraft(props.userId, props.flow.workflowId);
      setPhase({ kind: 'done', result: res.body });
      // V1/V2 dismiss straight to the list banner on success; V3 keeps the overlay
      // on its P5 success page and defers this call to P5's Finish button, so the
      // page is not torn down before it renders. See `deferCompletion`.
      if (!props.deferCompletion) props.onUploaded(res.body);
      return;
    }

    // EVERY failure path below keeps the draft — the builder's work survives a failed upload.
    if (res.kind === 'unauthorized') {
      setErrorMsg('Your session has expired. Sign in again — your draft is saved.');
      return;
    }
    if (res.status === 422 && res.code === 'component_docs_incomplete') {
      const detail = res.detail as unknown as ComponentDocsIncomplete | undefined;
      const missing = detail?.missing ?? [];
      setErrorMsg(
        missing.length > 0
          ? `${missing.length} ${missing.length === 1 ? 'step' : 'steps'} still need explaining: ${missing.map((m) => m.numbering).join(', ')}.`
          : 'Every step needs an explanation before you can share this.',
      );
      goToStage('instructions');
      if (missing[0]) focusComponent(missing[0].id);
      return;
    }
    if (res.status === 422 && res.code === 'connector_setup_notes_required') {
      setErrorMsg('This flow uses connectors — explain how to set them up in Details.');
      goToStage('details');
      setTriedStage((t) => ({ ...t, details: true }));
      focusDetailField('requirements');
      return;
    }
    if (res.status === 422 && res.code === 'upload_no_clientdata') {
      setErrorMsg('That flow has no stored definition we can capture, so it cannot be shared.');
      return;
    }
    if (res.status === 409) {
      setErrorMsg(
        'Sign out and sign back in so the Dataverse permission is granted, then retry. Your draft is saved.',
      );
      return;
    }
    if (res.status === 400 && res.code === 'invalid_request') {
      const fields = Array.isArray(res.detail) ? (res.detail as string[]) : [];
      setErrorMsg(`Please check these fields: ${fields.join(', ') || 'some fields'}.`);
      goToStage('details');
      setTriedStage((t) => ({ ...t, details: true }));
      return;
    }
    if (res.status === 400 && res.code === 'too_many_tags') {
      setErrorMsg('Up to 5 tags.');
      goToStage('details');
      return;
    }
    setErrorMsg(
      `Upload failed (${res.status}${res.code ? `: ${res.code}` : ''}). Your draft is saved.`,
    );
  }

  /**
   * Keyboard handling for the modal: Escape closes (keeping the draft), and Tab is cycled within the
   * dialog so focus cannot wander onto the page behind it.
   */
  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      props.onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    const visible = Array.from(focusable).filter((el) => el.offsetParent !== null || el === root);
    if (visible.length === 0) return;
    const first = visible[0]!;
    const last = visible[visible.length - 1]!;
    if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  }

  const stageIndex = STAGE_ORDER.indexOf(stage);

  // ── Callbacks that used to be inline in the JSX, pulled up so both skins wire them identically ───

  /** Instructions: expand/collapse one component's explanation panel. */
  function onToggleCollapse(id: string) {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Details: register a field element for focus/scroll on validation failure. */
  function registerDetailField(field: DetailsField, el: HTMLElement | null) {
    detailFieldRefs.current[field] = el;
  }

  /** Instructions: register a component's textarea for focus/scroll on validation failure. */
  function registerComponentField(componentId: string, el: HTMLTextAreaElement | null) {
    componentFieldRefs.current.set(componentId, el);
  }

  /** Details onChange: patch the draft's details. */
  function onDetailsChange(patch: Partial<DraftDetails>) {
    updateDraft((d) => ({ ...d, details: { ...d.details, ...patch } }));
  }

  /** Instructions onChange: set one component's explanation. */
  function onExplanationChange(componentId: string, explanation: string) {
    updateDraft((d) => ({
      ...d,
      explanations: { ...d.explanations, [componentId]: explanation },
    }));
  }

  /** Review: set the attestation tick (the hard data-safety gate). */
  function onAttestChange(attested: boolean) {
    updateDraft((d) => ({ ...d, attested }));
  }

  /**
   * Review: set the "I take responsibility" tick. NON-BLOCKING and independent of the gate above —
   * ticked publishes straight to Draft (the builder vouches), unticked holds the tool Pending for the
   * AI SWAT team to verify before it goes Approved. Defaults false (the safe, verified route).
   */
  function onResponsibilityChange(takesResponsibility: boolean) {
    updateDraft((d) => ({ ...d, takesResponsibility }));
  }

  /** Review: "edit this step" — jump to Instructions and focus the component. */
  function onEditComponent(componentId: string) {
    goToStage('instructions');
    focusComponent(componentId);
  }

  return {
    // Props passed through for rendering identity/preview and titles.
    props,
    // Derived + state the JSX reads.
    phase,
    components,
    draft,
    stage,
    details,
    explanations,
    requirementsRequired,
    detailErrors,
    missingIds,
    explained,
    instructionsComplete,
    // UP-F45: mode-aware submit label + the pre-press outstanding-items hint (never a gate).
    moderationMode,
    outstandingItems,
    triedStage,
    submitting,
    errorMsg,
    droppedCount,
    collapsedIds,
    pendingDuplicates,
    stageIndex,
    // Refs the JSX attaches.
    dialogRef,
    // Handlers.
    onResume,
    onStartNew,
    onDuplicatesContinue,
    onDuplicatesAbandon,
    goToStage,
    onNextFromDetails,
    onNextFromInstructions,
    onUpload,
    onKeyDown,
    onToggleCollapse,
    registerDetailField,
    registerComponentField,
    onDetailsChange,
    onExplanationChange,
    onAttestChange,
    onResponsibilityChange,
    onEditComponent,
  };
}

export type UploadOverlayVm = ReturnType<typeof useUploadOverlay>;

/**
 * Turn an inspect-components failure into something a builder can act on.
 *
 * THIS IS ALSO THE PREVIEW ART'S HONEST-ABSENCE PATH, and it is the reason the wizard's preview plates
 * need no "we could not read this flow" caption of their own. Every one of the three
 * `FlowComponentOutcome` failure reasons — `parse_error`, `no_definition`, `no_components` — arrives
 * here and ends in `phase: 'unavailable'` with the sentence below, so the wizard never reaches
 * `kind: 'wizard'` and no preview card is ever mounted without a manifest behind it. An emblem on a
 * plate with nothing explaining it is a state that cannot occur.
 *
 * The guarantee holds from the walker outward, not just by convention:
 * `packages/api/src/dataverse/components.ts` returns `no_components` for BOTH an empty walk
 * (`out.length === 0`) and a truncated one, so `ok: true` implies at least one component. And every
 * path to `kind: 'wizard'` runs `setComponents` first. The art still degrades to the emblem on an empty
 * list — `resolveModel` in design/FlowArt.tsx treats "no steps" and "unreadable" identically — but that
 * is a belt-and-braces fallback here rather than a reachable state.
 *
 * A flow between MAX_COMPONENTS (500, the walker's ceiling) and MAX_PREVIEW_NODES (64, the preview's)
 * DOES reach the wizard, and is the case the art handles itself: the diagram truncates and draws an
 * explicit overflow tick with a `+N` numeral rather than silently dropping steps.
 */
export function describeInspectFailure(
  status: number,
  code: string | null,
  detail: unknown,
): string {
  if (code === 'flow_components_unavailable') {
    const reason = (detail as { reason?: string } | null)?.reason;
    if (reason === 'no_components') {
      return 'This automation has no steps we can document yet. Add a trigger and at least one action in Power Automate, then try again.';
    }
    if (reason === 'no_definition' || reason === 'parse_error') {
      return 'We could not read the steps of this automation. Its stored definition is not in a format we can inspect, so it cannot be documented step by step.';
    }
    return 'We could not read the steps of this automation.';
  }
  if (code === 'upload_no_clientdata') {
    return 'This automation has no stored definition, so there is nothing to share. Open it in Power Automate, save it, then try again.';
  }
  if (code === 'upload_clientdata_compressed_unsupported') {
    return "This automation's definition is stored in a compressed form we cannot read yet.";
  }
  if (status === 409) {
    return 'Sign out and sign back in so the Dataverse permission is granted, then try again.';
  }
  if (status === 404) {
    return 'This part of the platform is not available in this build.';
  }
  return `We could not read this automation (${status}${code ? `: ${code}` : ''}).`;
}
